import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import ReactGA4 from "react-ga4";

import { AppointmentState, ServiceOption } from "../types";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import {
  parseQueryString,
  sendToWindowDataLayer,
  captureBooking,
} from "../utils/index";
import { fetchFullSiteData } from "../functions/fetchFullSiteData";
import { buttonPrimary, buttonSecondary } from "css";
import { UaEventOptions } from "react-ga4/types/ga4";
import { NextBackButtons } from "components";

interface ServiceOptionsProps {
  appointment: AppointmentState;
}

interface QueryStringParams {
  service?: string;
  shopId?: string;
  [key: string]: string | undefined;
}

const ServiceOptions: React.FC<ServiceOptionsProps> = ({ appointment }) => {
  const [clear, setClear] = useState<boolean>(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [queryString, setQueryString] = useState<QueryStringParams>(
    parseQueryString(window.location)
  );
  const [loading, setLoading] = useState<boolean>(true);

  const dispatch = useDispatch();

  // Determine if multiple selection is allowed based on version
  const isMultiSelectAllowed = appointment?.shopData?.configVersion === "v2";

  const handleServiceClick = (serviceOption: ServiceOption) => {
    const isSelected = selectedServices.includes(serviceOption.name);

    // For v1, replace the selection; for v2, toggle the selection
    const updatedServices = isMultiSelectAllowed
      ? isSelected
        ? selectedServices.filter((svc) => svc !== serviceOption.name)
        : [...selectedServices, serviceOption.name]
      : [serviceOption.name]; // For v1, only allow a single service

    setSelectedServices(updatedServices);

    dispatch(
      updateAppointment({
        updates: {
          services: updatedServices.map((name) =>
            serviceOptions.find((svc) => svc.name === name)
          ) as ServiceOption[],
          serviceHours: appointmentDuration
            ? appointmentDuration / 60
            : updatedServices.reduce(
                (sum, name) =>
                  sum +
                  Number(
                    serviceOptions.find((svc) => svc.name === name)?.hours || 0
                  ),
                0
              ),
          dropOffOnly: serviceOption.dropOffOnly,
          skipToForm: serviceOption.skipToForm,
        },
      })
    );

    document.getElementById("next")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setClear(true);
  };

  let serviceOptions: ServiceOption[] = [];
  const imgPath = "https://bdm-files.s3.amazonaws.com/images/service-icons/";

  const appointmentDuration =
    appointment?.shopData?.useServiceHoursAsApptHours &&
    appointment?.shopData?.staticAppointmentLength
      ? appointment?.shopData?.staticAppointmentLength
      : undefined;

  if (appointment?.shopData?.services) {
    serviceOptions = appointment.shopData.services; // Custom Options
  } else if (appointment?.shopData?.serviceOptions) {
    serviceOptions = appointment.shopData.serviceOptions as ServiceOption[]; // Default Options
  }

  // Initialize selectedServices from appointment on component mount
  useEffect(() => {
    if (appointment?.services?.length) {
      setSelectedServices(appointment.services.map((svc) => svc.name));
    } else if (appointment?.service) {
      setSelectedServices([appointment.service]);
    }
  }, []);

  const captureUTM = (
    queryParams: QueryStringParams
  ): Record<string, string> => {
    const filteredData = Object.fromEntries(
      Object.entries(queryParams).filter(([key]) => key.startsWith("utm_"))
    );

    ReactGA4.event({
      category: "Appointments",
      action: "scheduler hit",
      label: "UTM DATA COLLECTED",
      utm_data: filteredData,
    } as UaEventOptions);

    return filteredData as Record<string, string>;
  };

  useEffect(() => {
    sendToWindowDataLayer(
      "dc-service-scheduler-hit",
      "dc-service-scheduler-hit"
    );

    if (!appointment?.gaUtmSent) {
      const utmData = captureUTM(queryString);

      dispatch(
        updateAppointment({
          updates: { gaUtmSent: true, utmData: utmData },
        })
      );
    }
  }, []);

  // Helper function to process services and dispatch updates
  const processServicesAndDispatch = (servicesList: ServiceOption[]) => {
    if (servicesList.length > 0) {
      // Calculate total hours from all selected services
      const totalHours = servicesList.reduce(
        (sum, service) => sum + (Number(service.hours) || 0),
        0
      );

      // Set name based on number of services
      const serviceName =
        servicesList.length > 1 ? "Multiple Services" : servicesList[0].name;

      // Check if any service has dropOffOnly set to true
      const isDropOffOnly = servicesList.some(
        (service) => service.dropOffOnly === true
      );

      dispatch(
        updateAppointment({
          updates: {
            service: serviceName,
            hours: totalHours,
            dropOffOnly: isDropOffOnly,
            services: servicesList,
          },
        })
      );

      setClear(true);
    }
  };

  // Handle query string service
  useEffect(() => {
    if (queryString?.service) {
      const selectedService = serviceOptions.find(
        (svc) => svc.name === queryString.service
      );

      if (selectedService) {
        // Initialize array with the service from queryString
        const initialServices = [selectedService];
        processServicesAndDispatch(initialServices);
      }
    }
  }, [queryString?.service, serviceOptions]);

  // Handle multiple selected services
  useEffect(() => {
    if (selectedServices.length > 0) {
      // Get all the selected service objects
      const selectedServiceObjects = selectedServices
        .map((serviceName) =>
          serviceOptions.find((svc) => svc.name === serviceName)
        )
        .filter(Boolean) as ServiceOption[];

      processServicesAndDispatch(selectedServiceObjects);
    }
  }, [selectedServices, serviceOptions]);

  useEffect(() => {
    const shopId = appointment.shopId || queryString.shopId || "";

    if (appointment.domain && shopId) {
      fetchFullSiteData(appointment.domain, shopId)
        .then((response) => {
          if (!appointment.bookingId) {
            dispatch(
              updateAppointment({
                updates: {
                  shopData: response,
                  bailed: false,
                },
              })
            );
          }
        })
        .then(() => {
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching site data:", error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedServices.length) {
      sendToWindowDataLayer("dc-service-option", selectedServices.join(", "));
    }
  }, [selectedServices]);

  useEffect(() => {
    // If we're on v1 but have multiple services selected, clear all but the first
    if (!isMultiSelectAllowed && selectedServices.length > 1) {
      const firstService = selectedServices[0];
      setSelectedServices([firstService]);

      // Find the service object for the first service
      const serviceObject = serviceOptions.find(
        (svc) => svc.name === firstService
      );

      if (serviceObject) {
        dispatch(
          updateAppointment({
            updates: {
              services: [serviceObject],
              service: serviceObject.name,
              hours: Number(serviceObject.hours) || 0,
              dropOffOnly: serviceObject.dropOffOnly || false,
            },
          })
        );
      }
    }
  }, [isMultiSelectAllowed, appointment?.shopData?.configVersion]);

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <center>
            <img
              src="https://bdm-files.s3.amazonaws.com/images/spinners/Gear@1x-0.3s-200px-200px.gif"
              alt="loading"
            />
            <br />
            <br />
            Fetching service options...
          </center>
        </div>
      ) : (
        <div>
          <div className="grid md:grid-cols-2 justify-items-center mb-5 gap-2 mx-6 pt-6">
            {serviceOptions?.map((serviceOption, index) => (
              <div
                service-id={`dc-service-${serviceOption.name
                  .replace(/\s+/g, "_")
                  .toLowerCase()}`}
                className={`w-full cursor-pointer ${
                  selectedServices.includes(serviceOption.name)
                    ? buttonSecondary
                    : buttonPrimary
                }`}
                onClick={() => handleServiceClick(serviceOption)}
                key={`${serviceOption.name}-${index}`}
                style={{
                  backgroundColor: selectedServices.includes(serviceOption.name)
                    ? appointment?.shopData?.color
                    : "white",

                  boxShadow:
                    appointment?.shopData?.features?.microsites?.bookings
                      ?.shadow === "shadow-md"
                      ? `0 4px 6px ${appointment?.shopData?.color}33`
                      : "none",

                  border: `1px solid ${appointment?.shopData?.color}`,

                  color: !selectedServices.includes(serviceOption.name)
                    ? "black"
                    : "white",
                }}
              >
                <div className="flex items-center pl-2">
                  <img
                    src={`${imgPath}${serviceOption.icon}`}
                    className="w-9 h-9"
                    style={{
                      paddingRight: "10px",
                      filter: selectedServices.includes(serviceOption.name)
                        ? "invert(100%)"
                        : undefined,
                    }}
                    alt={serviceOption.name}
                  />
                </div>
                <div className="grid w-full gap-2">
                  {serviceOption.name}
                  <br />
                  <span style={{ fontSize: "9pt" }}>
                    {serviceOption.description}
                  </span>
                </div>
                {isMultiSelectAllowed && (
                  <div className="flex items-center pr-2">
                    <input
                      type="checkbox"
                      checked={selectedServices.includes(serviceOption.name)}
                      onChange={() => {}} // Handled by parent onClick
                      className="w-4 h-4 rounded border-2"
                      style={{
                        accentColor: appointment?.shopData?.color,
                        borderColor: appointment?.shopData?.color,
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div id="next">
            <NextBackButtons appointment={appointment} clear={clear} />
          </div>
        </div>
      )}
    </>
  );
};

export { ServiceOptions };
